/**
 * Help — renders the Mission Control user guide in-app so fleet users can reach
 * it without repo access. The markdown source is bundled (?raw) from
 * src/content/mission-control-user-guide.md (the canonical copy lives in the repo
 * at documentation/mission-control-user-guide.md — keep the two in sync).
 *
 * No Tailwind typography plugin is installed, so we style the rendered elements
 * with a components map of utility classes.
 */
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import guideMarkdown from '../content/mission-control-user-guide.md?raw'

const components: Components = {
  h1: ({ children }) => <h1 className="text-2xl font-semibold text-gray-900 mt-2 mb-3">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-2 pb-1 border-b border-gray-200">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-gray-800 mt-5 mb-1.5">{children}</h3>,
  p: ({ children }) => <p className="text-sm text-gray-700 leading-relaxed my-2">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 my-2 space-y-1 text-sm text-gray-700">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 my-2 space-y-1 text-sm text-gray-700">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ href, children }) => (
    <a href={href} className="text-indigo-600 hover:underline" target={href?.startsWith('http') ? '_blank' : undefined} rel="noreferrer">
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-indigo-200 bg-indigo-50/40 px-4 py-2 my-3 text-sm text-gray-600 rounded-r">{children}</blockquote>
  ),
  hr: () => <hr className="my-6 border-gray-200" />,
  code: ({ children }) => <code className="font-mono text-xs bg-gray-100 text-gray-800 rounded px-1 py-0.5">{children}</code>,
  pre: ({ children }) => (
    <pre className="bg-gray-900 text-white rounded-lg p-3 my-3 overflow-x-auto text-xs leading-relaxed">{children}</pre>
  ),
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto">
      <table className="w-full text-sm border border-gray-200 rounded-lg border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
  th: ({ children }) => <th className="text-left font-semibold text-gray-700 px-3 py-2 border-b border-gray-200">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 border-b border-gray-100 text-gray-700 align-top">{children}</td>,
}

export function Help() {
  return (
    <div className="max-w-3xl mx-auto">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {guideMarkdown}
      </ReactMarkdown>
    </div>
  )
}
